import { DefaultTheme, styled } from 'styled-components';
import { InformationType } from '../types';

export interface InformationMessageProps {
    $informationType: InformationType;
    $hasMessage: boolean;
    /**
     * The field is invalid, whether or not it carries a message.
     *
     * <p>Error styling used to be reachable only through `$hasMessage`, which made the two
     * inseparable: a field could not be marked invalid without also rendering a message, and
     * rendering one changes the field's height. Callers that wanted a stable layout had to give up
     * the error state entirely, or reserve the line with a blank message — which, because the
     * border follows `$hasMessage`, left the field permanently outlined in the info colour.
     */
    $invalid?: boolean;
}

export function getColorByInfoType(theme: DefaultTheme, informationType: InformationType) {
    return informationType === InformationType.INFO
        ? theme.inputMessageColorInfo
        : theme.inputMessageColorError;
}

/**
 * Error/info message utilities for input components.
 *
 * Implements part of https://www.figma.com/design/6ZVGZowOXbrOXdrcQ9ji7y/Design-system?node-id=137-8279&m=dev
 */
export const Message = styled.span<Pick<InformationMessageProps, '$informationType'>>`
    display: block;
    color: ${({ theme, $informationType }) => getColorByInfoType(theme, $informationType)};
    font-size: ${({ theme }) => theme.spacing(3)};
    line-height: ${({ theme }) => theme.spacing(4)};
    margin-top: ${({ theme }) => theme.spacing(1)};
`;

/** How long the message line takes to open or close. InputMessage releases a cleared message after it. */
export const MESSAGE_TRANSITION_MS = 160;

/**
 * Opens and closes the message line. 0fr to 1fr is the one way to transition to a height nothing
 * has measured; visibility lands only after the collapse so the text fades rather than vanishes.
 */
export const MessageRoot = styled.div<{ $shown: boolean }>`
    display: grid;
    grid-template-rows: ${({ $shown }) => ($shown ? '1fr' : '0fr')};
    opacity: ${({ $shown }) => ($shown ? 1 : 0)};
    visibility: ${({ $shown }) => ($shown ? 'visible' : 'hidden')};
    transition:
        grid-template-rows ${MESSAGE_TRANSITION_MS}ms ease,
        opacity ${MESSAGE_TRANSITION_MS}ms ease,
        visibility 0s linear ${({ $shown }) => ($shown ? '0s' : `${MESSAGE_TRANSITION_MS}ms`)};

    @media (prefers-reduced-motion: reduce) {
        transition: none;
    }
`;

/**
 * The grid item. Unpadded so the row can reach zero height, and overflow hidden so it is a block
 * formatting context: Message's margin-top then stays inside and collapses with the row, instead of
 * escaping as a gap under every field that has ever shown a message.
 */
export const MessageViewport = styled.div`
    min-height: 0;
    overflow: hidden;
`;
